import { FunctionComponent, ReactNode } from "react";
import styles from "./SectionMessage.module.css";
import { Link, LinkType } from "../links";
import {
  LabelPairedXmarkCaptionBoldIcon,
  StandaloneCircleCheckBoldIcon,
  StandaloneCircleExclamationBoldIcon,
  StandaloneCircleInfoBoldIcon,
  StandalonePlaceholderBoldIcon,
  StandaloneTriangleExclamationBoldIcon,
} from "@deriv/quill-icons";
import IconButton from "../buttons/icon";
import { BaseButtonType } from "../buttons/base";
import { BasicButton } from "../buttons/basic";
export type SectionMessageType = {
  className?: string;
  message?: string;
  title?: string;
  icon?: string;
  showIcon?: boolean;
  showCloseButton?: boolean;
  buttonPrimary?: BaseButtonType;
  buttonSecondary?: BaseButtonType;
  onClickClose?: () => void;

  /** Variant props */
  indicator?: "danger" | "warning" | "success" | "information" | "default";
  size?: "sm" | "md";

  /** Custom props */
  customIcon?: ReactNode;
  link1?: Omit<LinkType, "color">;
  link2?: Omit<LinkType, "color">;
};

export const SectionMessage: FunctionComponent<SectionMessageType> = ({
  className = "",
  indicator = "danger",
  size = "sm",
  message = "",
  title = "",
  customIcon,
  showIcon = true,
  showCloseButton = false,
  link1,
  link2,
  buttonPrimary,
  buttonSecondary,
  onClickClose,
}) => {
  const sectionMessageId = `quill-section-message-${indicator}-${size}`;

  const defaultIcons = {
    danger: StandaloneTriangleExclamationBoldIcon,
    warning: StandaloneCircleExclamationBoldIcon,
    success: StandaloneCircleCheckBoldIcon,
    information: StandaloneCircleInfoBoldIcon,
    default: StandalonePlaceholderBoldIcon,
  } as const;

  const IconComponent = defaultIcons[indicator] as React.ComponentType;

  return (
    <div
      className={[styles.root, "quill-ui-next-component", className].join(" ")}
      data-id={sectionMessageId}
      data-indicator={indicator}
      data-size={size}
    >
      {showIcon && (
        <div className={styles.fixIcon}>
          {indicator === "default" && customIcon
            ? customIcon
            : IconComponent && (
                // @ts-ignore
                <IconComponent iconSize={size === "md" ? "md" : "sm"} />
              )}
        </div>
      )}
      <div className={styles.titleMessageButtons}>
        <div className={styles.titleMessage}>
          {title && <b className={styles.title}>{title}</b>}
          <div className={styles.containerMessage}>
            <div
              className={`${styles.message} ${
                !title ? styles.noTitleMessage : ""
              }`}
            >
              {message}
            </div>
          </div>
        </div>
        {link1 ? (
          <div className={styles.link1Link2}>
            <Link {...link1} size={size} color="blackWhite" />
            {link2 && <Link {...link2} size={size} color="blackWhite" />}
          </div>
        ) : (
          <div className={styles.buttonContainer}>
            {buttonPrimary && (
              <BasicButton
                {...buttonPrimary}
                style="secondary"
                color="blackWhite"
              />
            )}
            {buttonSecondary && (
              <BasicButton
                {...buttonSecondary}
                style="primary"
                color="blackWhite"
                size={size === "md" ? "md" : "sm"}
              />
            )}
          </div>
        )}
      </div>
      {showCloseButton && (
        <IconButton
          color="blackWhite"
          icon={<LabelPairedXmarkCaptionBoldIcon />}
          style="tertiary"
          onClick={onClickClose}
        />
      )}
    </div>
  );
};

SectionMessage.displayName = "SectionMessage";

export default SectionMessage;
